import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { recordPayment, initiateMpesaPayment, formatKES } from '../../../services/invoiceService';

const PAYMENT_METHODS = [
  { value: 'mpesa', label: 'M-Pesa', icon: 'Smartphone' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: 'Building2' },
  { value: 'card', label: 'Card', icon: 'CreditCard' },
  { value: 'cash', label: 'Cash', icon: 'Banknote' },
  { value: 'cheque', label: 'Cheque', icon: 'FileText' }
];

const PaymentModal = ({ invoice, onClose, onPaymentRecorded }) => {
  const [form, setForm] = useState({
    amount: invoice?.remainingBalance || invoice?.total || '',
    paymentMethod: 'mpesa',
    paymentDate: new Date()?.toISOString()?.split('T')?.[0],
    referenceNumber: '',
    mpesaPhone: '',
    mpesaTransactionId: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [mpesaResult, setMpesaResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const remainingBalance = invoice?.remainingBalance || parseFloat(invoice?.total || 0) - parseFloat(invoice?.paidAmount || 0);

  const handleMpesaSTK = async () => {
    if (!form?.mpesaPhone) { setError('Enter M-Pesa phone number'); return; }
    if (!form?.amount) { setError('Enter payment amount'); return; }
    try {
      setMpesaLoading(true);
      setError(null);
      const result = await initiateMpesaPayment(invoice?.id, form?.mpesaPhone, form?.amount);
      setMpesaResult(result);
      if (result?.transactionId) {
        setForm(f => ({ ...f, mpesaTransactionId: result?.transactionId }));
      }
    } catch (err) {
      setError(err?.message || 'M-Pesa request failed');
    } finally {
      setMpesaLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form?.amount || parseFloat(form?.amount) <= 0) { setError('Enter a valid amount'); return; }
    if (parseFloat(form?.amount) > remainingBalance + 0.01) {
      setError(`Amount cannot exceed remaining balance of ${formatKES(remainingBalance)}`);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await recordPayment(invoice?.id, form);
      setSuccess('Payment recorded successfully!');
      setTimeout(() => {
        onPaymentRecorded?.();
        onClose();
      }, 1200);
    } catch (err) {
      setError(err?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-heading-semibold text-text-primary">Record Payment</h2>
            <p className="text-xs text-text-secondary mt-0.5">{invoice?.invoiceNumber}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary-50 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Invoice Summary */}
          <div className="bg-secondary-50 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs text-text-secondary">Invoice Total</p>
              <p className="text-sm font-heading-semibold text-text-primary">{formatKES(invoice?.total)}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary">Paid</p>
              <p className="text-sm font-heading-semibold text-emerald-600">{formatKES(invoice?.paidAmount)}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary">Remaining</p>
              <p className="text-sm font-heading-semibold text-amber-600">{formatKES(remainingBalance)}</p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm">
              <Icon name="CheckCircle" size={16} />
              {success}
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">Payment Method</label>
            <div className="grid grid-cols-5 gap-2">
              {PAYMENT_METHODS?.map(method => (
                <button
                  key={method?.value}
                  onClick={() => setForm(f => ({ ...f, paymentMethod: method?.value }))}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-colors text-xs ${
                    form?.paymentMethod === method?.value
                      ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary-200 text-text-secondary'
                  }`}
                >
                  <Icon name={method?.icon} size={18} />
                  {method?.label}
                </button>
              ))}
            </div>
          </div>

          {/* M-Pesa STK Push */}
          {form?.paymentMethod === 'mpesa' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="Smartphone" size={16} className="text-green-600" />
                <span className="text-sm font-body-medium text-green-700">M-Pesa STK Push</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={form?.mpesaPhone}
                  onChange={e => setForm(f => ({ ...f, mpesaPhone: e?.target?.value }))}
                  placeholder="254712345678"
                  className="flex-1 border border-green-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <button
                  onClick={handleMpesaSTK}
                  disabled={mpesaLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-body-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {mpesaLoading ? <Icon name="Loader" size={14} className="animate-spin" /> : <Icon name="Send" size={14} />}
                  {mpesaLoading ? 'Sending...' : 'Send STK'}
                </button>
              </div>
              {mpesaResult && (
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-700 font-body-medium">{mpesaResult?.message}</p>
                  <p className="text-xs text-text-secondary mt-1">Transaction ID: {mpesaResult?.transactionId}</p>
                </div>
              )}
              <div>
                <label className="block text-xs text-text-secondary mb-1">M-Pesa Transaction ID (after payment)</label>
                <input
                  type="text"
                  value={form?.mpesaTransactionId}
                  onChange={e => setForm(f => ({ ...f, mpesaTransactionId: e?.target?.value }))}
                  placeholder="e.g. QGH7XXXXXXX"
                  className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>
            </div>
          )}

          {/* Amount & Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1.5">Amount (KES) *</label>
              <input
                type="number"
                value={form?.amount}
                onChange={e => setForm(f => ({ ...f, amount: e?.target?.value }))}
                min="0"
                step="0.01"
                max={remainingBalance}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-text-secondary mt-1">Max: {formatKES(remainingBalance)}</p>
            </div>
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1.5">Payment Date</label>
              <input
                type="date"
                value={form?.paymentDate}
                onChange={e => setForm(f => ({ ...f, paymentDate: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-1.5">Reference Number</label>
            <input
              type="text"
              value={form?.referenceNumber}
              onChange={e => setForm(f => ({ ...f, referenceNumber: e?.target?.value }))}
              placeholder="Bank ref, cheque number, etc."
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-1.5">Notes</label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
              rows={2}
              placeholder="Optional payment notes"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-body-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !!success}
            className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? <Icon name="Loader" size={16} className="animate-spin" /> : <Icon name="CheckCircle" size={16} />}
            {saving ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
